import * as Sentry from '@sentry/react';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useInitiativeTasks } from '../../../../api/initiative-client/initiative-client.hooks';
import { InitiativeTasksFilters } from '../../../../api/initiative-client/initiative-client.type';
import { useGoalTasks, useOutcomeTasks } from '../../../../api/strategy-client/strategy-client.hooks';
import type { MinifiedTask } from '../../../../api/tasks-client/task-client.type';
import { useEpicTasks } from '../../../../api/tasks-client/tasks-client.hooks';
import { useWorkPeriodsTasksSingular } from '../../../../api/work-periods-client/work-periods-client.hooks';
import { Measure, TimeAllocationType } from '../../../../api/work-periods-client/work-periods-client.type';
import { formatDate, formatToISODate } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useRelatedTasksTabStore, useStrategyStore } from '../../../../store/strategy-store/strategy-store';
import {
  setRelatedTasksData,
  setRelatedTasksPage,
  setRelatedTasksSelectedPhase,
  setRelatedTasksSelectedTypes,
} from '../../../../store/strategy-store/strategy-store.actions';
import { useCurrentStrategyTab } from '../../strategy.hooks';
import { StrategyTab } from '../../strategy.type';
import { collectTaskTypes, filterTasksBySearchTerm } from './related-tasks-tab.helpers';
import { RelatedTasksMode } from './related-tasks-tab.type';

/**
 * Stable empty task list. `setRelatedTasksData` is an unguarded `setState`, so passing a
 * fresh `[]` re-renders every `state.tasks` subscriber even when the slot already holds one.
 */
const EMPTY_TASKS: MinifiedTask[] = [];

/**
 * Hook that initiates API calls to fetch related tasks data.
 *
 * @returns {Object} Object containing combined loading state
 * @returns {boolean} returns.isLoading - True if either API is still loading
 */
const useRelatedTasksData = (): { isLoading: boolean } => {
  const { isLoading: isLoadingInitiativeTasks } = useInitiativeTasksAPI();
  const { isLoading: isLoadingWorkPeriodsTasks } = useWorkPeriodsTasksAPI();

  return {
    isLoading: isLoadingWorkPeriodsTasks || isLoadingInitiativeTasks,
  };
};

/**
 * Hook that fetches and manages initiative tasks data via the initiative tasks API.
 *
 * @returns {Object} Object containing loading state
 * @returns {boolean} returns.isLoading - True if the API request is in progress
 */
const useInitiativeTasksAPI = (): { isLoading: boolean } => {
  const currentStrategyTab = useCurrentStrategyTab();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const selectedDate = useRelatedTasksTabStore((state) => state.selectedDate);
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const selectedTeam = useRelatedTasksTabStore((state) => state.selectedTeam);
  const selectedPhase = useRelatedTasksTabStore((state) => state.selectedPhase);
  const selectedOutcome = useRelatedTasksTabStore((state) => state.selectedOutcome);
  const relatedTasksData = useRelatedTasksTabStore((state) => state.tasks);

  const filter = useFilterParam();
  const entityId = initiative?.id;
  const hasEntity = Boolean(entityId);
  const isEnabled = !selectedPhase && Boolean(selectedDate) && hasEntity;

  // Prepare filters for all entity types
  const initiativeFilters = {
    portfolioId: portfolio?.id,
    initiativeId: entityId,
    filter,
    date: selectedDate ? formatDate(selectedDate, { format: 'YYYY-MM' }) : null,
    epicId: mode === RelatedTasksMode.ContributingEpics ? selectedEpic : null,
    projectId: mode === RelatedTasksMode.ContributingTeams ? selectedTeam : null,
  };

  const outcomeFilters = {
    portfolioId: undefined,
    initiativeId: undefined,
    filter,
    date: selectedDate ? formatDate(selectedDate, { format: 'YYYY-MM' }) : null,
    epicId: mode === RelatedTasksMode.ContributingEpics ? selectedEpic : null,
    projectId: mode === RelatedTasksMode.ContributingTeams ? selectedTeam : null,
  };

  const goalFilters = {
    portfolioId: undefined,
    initiativeId: undefined,
    filter,
    date: selectedDate ? formatDate(selectedDate, { format: 'YYYY-MM' }) : null,
    epicId: mode === RelatedTasksMode.ContributingEpics ? selectedEpic : null,
    projectId: mode === RelatedTasksMode.ContributingTeams ? selectedTeam : null,
    outcomeId: selectedOutcome ?? null,
  };

  const epicFilters = {
    portfolioId: undefined,
    initiativeId: undefined,
    epicId: null,
    filter,
    date: selectedDate ? formatDate(selectedDate, { format: 'YYYY-MM' }) : null,
    projectId: mode === RelatedTasksMode.ContributingTeams ? selectedTeam : null,
  };

  // Conditionally call the appropriate hook based on strategy tab
  const initiativeResult = useInitiativeTasks(initiativeFilters, {
    enabled: isEnabled && currentStrategyTab === StrategyTab.Initiatives && hasEntity,
    staleTime: 0,
    gcTime: 0,
  });

  const outcomeResult = useOutcomeTasks(
    currentStrategyTab === StrategyTab.Outcomes ? entityId : undefined,
    outcomeFilters,
    {
      enabled: isEnabled && currentStrategyTab === StrategyTab.Outcomes && hasEntity,
      staleTime: 0,
      gcTime: 0,
    },
  );

  const goalResult = useGoalTasks(currentStrategyTab === StrategyTab.Goals ? entityId : undefined, goalFilters, {
    enabled: isEnabled && currentStrategyTab === StrategyTab.Goals && hasEntity,
    staleTime: 0,
    gcTime: 0,
  });

  const epicResult = useEpicTasks(currentStrategyTab === StrategyTab.Epics ? entityId : undefined, epicFilters, {
    enabled: isEnabled && currentStrategyTab === StrategyTab.Epics && hasEntity,
    staleTime: 0,
    gcTime: 0,
  });

  // Extract data and query based on current strategy tab
  let taskData: MinifiedTask[] = [];
  let query;

  if (currentStrategyTab === StrategyTab.Initiatives) {
    taskData = initiativeResult.data?.[0]?.tasks ?? [];
    query = initiativeResult.query;
  } else if (currentStrategyTab === StrategyTab.Outcomes) {
    taskData = outcomeResult.data ?? [];
    query = outcomeResult.query;
  } else if (currentStrategyTab === StrategyTab.Goals) {
    taskData = goalResult.data ?? [];
    query = goalResult.query;
  } else if (currentStrategyTab === StrategyTab.Epics) {
    taskData = epicResult.tasks ?? [];
    query = epicResult.query;
  } else {
    query = initiativeResult.query; // fallback
  }

  useDeepCompareEffect(() => {
    if (taskData && !isEqual(taskData, relatedTasksData) && isEnabled) {
      const taskTypes = collectTaskTypes(taskData);

      setRelatedTasksPage(1);
      setRelatedTasksSelectedTypes(taskTypes);
      setRelatedTasksData(taskData);
    }
  }, [taskData, relatedTasksData, isEnabled]);

  useEffect(() => {
    if (query?.error) {
      setRelatedTasksData([]);
      Sentry.captureException(query.error);
    }
  }, [query?.error]);

  return {
    isLoading: query?.isFetching ?? false,
  };
};

/**
 * Hook that fetches and manages work periods tasks data via the work periods tasks API.
 *
 * Only runs under initiative scope. The work-periods scope resolver branches on
 * subproject, project, portfolio, work-period and initiative ids — it has no goal or
 * outcome branch — while `initiativePerformance` also holds goals and outcomes (as
 * untyped shallow copies). Sending one of those as `initiative_id` cannot resolve and
 * is rejected, so the phase drill-down is disabled outside the Initiatives tab rather
 * than issuing a request the API cannot answer today. The status the user picked stays
 * selected and the list renders empty, which is the truthful result: only the initiative
 * branch translates these seven phase names, so goal/outcome scope returns nothing for
 * most of them. That is a tractable backend follow-up rather than a hard limit — check
 * before assuming it still holds.
 *
 * @returns {Object} Object containing loading state
 * @returns {boolean} returns.isLoading - True if the API request is in progress
 */
const useWorkPeriodsTasksAPI = (): { isLoading: boolean } => {
  const { initiativeId } = useParams();
  const currentStrategyTab = useCurrentStrategyTab();
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const selectedDate = useRelatedTasksTabStore((state) => state.selectedDate);
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const selectedPhase = useRelatedTasksTabStore((state) => state.selectedPhase);
  const useGroups = useRelatedTasksTabStore((state) => state.useGroups);
  const relatedTasksData = useRelatedTasksTabStore((state) => state.tasks);

  // Which surface we are on. Route-derived, and stable across the entity slot loading.
  const isInitiativeScope = currentStrategyTab === StrategyTab.Initiatives;
  // Whether the slot really holds the routed initiative. Cross-checks store *content*
  // against the route *param*, so unlike the tab check it does not share a failure mode
  // with itself: a foreign entity cannot satisfy it just because the path changed early.
  const isRoutedInitiative = Boolean(initiativeId) && initiative?.id === initiativeId;
  const hasPhase = Boolean(selectedPhase);
  // `filters-epics.tsx` is the only filter panel that renders a status control, so no other
  // mode can offer one. Note the converse does not hold: `side-filters.tsx` unmounts the
  // whole panel when collapsed, so this being true does not mean a control is visible.
  const hasPhaseControl = mode === RelatedTasksMode.ContributingEpics;
  // `selectedDate` is part of `measure_filter`, and `getLatestDate` returns null for an
  // initiative with no task history, so it can stay null indefinitely. Without this guard
  // the filter serialises to "<phase>," and the endpoint returns a 500 ("cannot unpack
  // non-iterable NoneType"); verified against the API.
  //
  // Gating here cannot strand a stale list, on two independent grounds. A date can never go
  // from valid back to null: the picker is `clearable={false}` with an `if (value)` guard,
  // the five `show*RelatedTasks` actions are all `if (date)` guarded, and the auto-init only
  // writes when the date is already falsy. And when there is no date from the start,
  // `useInitiativeTasksAPI` above cannot have populated `tasks` either, because it requires
  // `selectedDate` too. Breaking either property alone would leave a previous month's rows
  // on screen under a status filter that was never applied to them.
  const isEnabled = Boolean(selectedDate) && hasPhase && isRoutedInitiative;

  const { data, query } = useWorkPeriodsTasksSingular({
    payload: {
      measure_name: [Measure.FlowByPhase],
      // Send initiative_id whenever we have one: it is what resolves a board for the epic's
      // phase mappings, and epic_id narrows the task set on top of it rather than replacing it.
      // Keyed on the identity check, not the tab, so a foreign uuid cannot be assembled into
      // the payload — and therefore cannot reach the query key either — during the commit
      // where the path has changed but the outgoing page has not yet cleared the slot.
      initiative_id: isRoutedInitiative ? initiative?.id : undefined,
      epic_id: selectedEpic ? selectedEpic : undefined,
      start_date:
        formatToISODate(initiative?.effective_start_date ? initiative.effective_start_date : initiative?.start_date) ??
        undefined,
      end_date:
        formatToISODate(initiative?.effective_end_date ? initiative.effective_end_date : initiative?.end_date) ??
        undefined,
      measure_filter: `${selectedPhase?.toLowerCase()},${formatDate(selectedDate, { format: 'YYYY-MM' })}`,
      time_allocation_type: TimeAllocationType.Monthly,
      use_groups: useGroups,
    },
    options: {
      enabled: isEnabled,
      staleTime: 0,
      gcTime: 0,
    },
  });

  useDeepCompareEffect(() => {
    if (data && !isEqual(data, relatedTasksData) && isEnabled) {
      setRelatedTasksPage(1);
      setRelatedTasksData(data);
    }
  }, [data, relatedTasksData, isEnabled]);

  // A status this scope cannot apply. `useInitiativeTasksAPI` is gated on `!selectedPhase`,
  // so without clearing the list the previous *unfiltered* tasks would stay on screen
  // implying the status had been applied.
  useEffect(() => {
    if (!hasPhase) {
      return;
    }

    if (!hasPhaseControl) {
      // No mode renders a control for it, so nothing on screen reveals or removes it. This
      // arm is deliberately scope-INDEPENDENT: on the Initiatives tab a phase set by the
      // Flow-by-Phase drill-down survives a mode switch, and the query stays enabled, so a
      // phase-filtered subset would render as the complete Related Work Items list.
      setRelatedTasksSelectedPhase(null);
      return;
    }

    if (!isInitiativeScope) {
      // Keep the user's chosen status and show an empty list. Resetting the dropdown for
      // them reads as a glitch, and empty is the truthful result for this scope.
      setRelatedTasksData(EMPTY_TASKS);
    }
  }, [hasPhase, isInitiativeScope, hasPhaseControl]);

  useEffect(() => {
    if (query.error) {
      setRelatedTasksData([]);
      Sentry.captureException(query.error);
    }
  }, [query.error]);

  return {
    isLoading: query.isFetching,
  };
};

/**
 * Hook that determines the appropriate initiative tasks filter based on the current mode and selections
 *
 * @returns {InitiativeTasksFilters} The determined filter value based on current state
 */
const useFilterParam = (): InitiativeTasksFilters => {
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const selectedThroughput = useRelatedTasksTabStore((state) => state.selectedThroughput);
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const selectedTeam = useRelatedTasksTabStore((state) => state.selectedTeam);

  const filter = useMemo(() => {
    switch (mode) {
      case RelatedTasksMode.ContributingTasks:
        return selectedThroughput as InitiativeTasksFilters;
      case RelatedTasksMode.ContributingEpics:
        return selectedEpic ? InitiativeTasksFilters.Epic : InitiativeTasksFilters.Completed;
      case RelatedTasksMode.ContributingTeams:
        return selectedTeam ? InitiativeTasksFilters.Project : InitiativeTasksFilters.Completed;
      default:
        return InitiativeTasksFilters.Completed;
    }
  }, [mode, selectedEpic, selectedTeam, selectedThroughput]);

  return filter;
};

/**
 * Hook that filters tasks based on the current mode and selected task types
 *
 * @returns {MinifiedTask[]} Array of filtered tasks.
 */
const useFilteredTasks = (): MinifiedTask[] => {
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const tasks = useRelatedTasksTabStore((state) => state.tasks);
  const selectedTypes = useRelatedTasksTabStore((state) => state.selectedTypes);

  const filteredTasks = useMemo(() => {
    let filteredByMode = tasks;

    switch (mode) {
      case RelatedTasksMode.ContributingTasks:
        filteredByMode = tasks.filter((task) => {
          return selectedTypes.includes(task.type);
        });
        break;
      default:
        filteredByMode = tasks;
    }

    return filteredByMode;
  }, [mode, selectedTypes, tasks]);

  return filteredTasks;
};

/**
 * Hook that searches tasks based on the current search term
 *
 * @returns {MinifiedTask[]} Array of searched tasks.
 */
const useSearchedTasks = (): MinifiedTask[] => {
  const searchTerm = useRelatedTasksTabStore((state) => state.searchTerm);

  const filteredTasks = useFilteredTasks();

  const searchedTasks = useMemo(() => {
    return filterTasksBySearchTerm(filteredTasks, searchTerm);
  }, [filteredTasks, searchTerm]);

  return searchedTasks;
};

/**
 * Hook that determines if data is available to display based on the current mode and loading state
 *
 * @param {boolean} isLoading - Whether data is currently being loaded
 * @returns {boolean} True if data is available to display, false otherwise
 */
const useDataAvailable = (isLoading: boolean): boolean => {
  const tasks = useRelatedTasksTabStore((state) => state.tasks);
  const mode = useRelatedTasksTabStore((state) => state.mode);
  const selectedEpic = useRelatedTasksTabStore((state) => state.selectedEpic);
  const selectedTeam = useRelatedTasksTabStore((state) => state.selectedTeam);

  if (mode === RelatedTasksMode.ContributingEpics) {
    return Boolean(selectedEpic) || tasks.length > 0;
  }

  if (mode === RelatedTasksMode.ContributingTeams) {
    return Boolean(selectedTeam) || tasks.length > 0;
  }

  return !isLoading && tasks.length > 0;
};

export { useDataAvailable, useFilteredTasks, useRelatedTasksData, useSearchedTasks };
